import { useEffect, useRef, useState } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import {
  useToast,
  ToastType,
} from '@/context_providers/toast_context/ToastContext';
import { Routes } from '@/enums/Routes';
import Loading from '@/components/loading/Loading';
import PageHeader from '@/components/utils/PageHeader';
import { Add } from '@mui/icons-material';
import {
  getAllPrototypeFirmwares,
  getAllPrototypers,
  formatPrototypeFirmwareError,
} from '@/repository/PrototypeFirmwareDAO';
import type { PrototypeFirmware } from '@/models/PrototypeFirmware/PrototypeFirmware';
import type { PrototyperUserInfo } from '@/models/PrototypeFirmware/PrototyperUserInfo';
import FirmwareTab from './components/FirmwareTab';
import ActiveProtoTypersTab from './components/ActiveProtoTypersTab';

const PrototypeFirmwareManagement = () => {
  type ActiveTab = 'firmware' | 'prototypers';
  const { isSafeguardDev, bearerToken } = useUserContext();
  const { showToast } = useToast();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<ActiveTab>('firmware');
  const [pageLoading, setPageLoading] = useState(true);
  const [firmwares, setFirmwares] = useState<PrototypeFirmware[]>([]);
  const [prototypers, setPrototypers] = useState<PrototyperUserInfo[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const showToastRef = useRef(showToast);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  useEffect(() => {
    if (!isSafeguardDev) history.replace(Routes.HOME);
  }, [isSafeguardDev, history]);

  const refetchFirmwares = async () => {
    try {
      const list = await getAllPrototypeFirmwares();
      setFirmwares(list);
    } catch (error) {
      const { message } = formatPrototypeFirmwareError(error, 'Firmware');
      showToastRef.current(message, ToastType.DANGER);
    }
  };

  const refetchPrototypers = async () => {
    try {
      const list = await getAllPrototypers();
      setPrototypers(list);
    } catch (error) {
      const { message } = formatPrototypeFirmwareError(error, 'Prototyper');
      showToastRef.current(message, ToastType.DANGER);
    }
  };

  useEffect(() => {
    if (!bearerToken || !isSafeguardDev) return;
    (async () => {
      setPageLoading(true);
      await Promise.all([refetchFirmwares(), refetchPrototypers()]);
      setPageLoading(false);
    })();
  }, [bearerToken, isSafeguardDev]);

  if (!isSafeguardDev) return null;

  return (
    <Container maxWidth="xl">
      {activeTab === 'firmware' ? (
        <PageHeader
          action={() => setAddModalOpen(true)}
          icon={<Add width={14} height={14} />}
          header={'Prototype Firmware'}
          label={'Upload Firmware'}
          disabled={false}
        />
      ) : (
        <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Prototype Firmware Management
          </Typography>
        </Box>
      )}
      <Tabs
        value={activeTab}
        onChange={(_, v: ActiveTab) => setActiveTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab value="firmware" label="Firmware" />
        <Tab value="prototypers" label="Active Prototypers" />
      </Tabs>
      {pageLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ height: '40vh' }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : activeTab === 'firmware' ? (
        <FirmwareTab
          firmwares={firmwares}
          prototypers={prototypers}
          addModalOpen={addModalOpen}
          onAddModalClose={() => setAddModalOpen(false)}
          refetchFirmwares={refetchFirmwares}
          refetchPrototypers={refetchPrototypers}
        />
      ) : (
        <ActiveProtoTypersTab
          prototypers={prototypers}
          refetchPrototypers={refetchPrototypers}
          refetchFirmwares={refetchFirmwares}
        />
      )}
    </Container>
  );
};

const PrototypeFirmwareManagementWithAuth = withAuthenticationRequired(
  PrototypeFirmwareManagement,
  {
    onRedirecting: () => <Loading loadingText={'Loading...'} />,
  },
);

export default PrototypeFirmwareManagementWithAuth;
